import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { buttonRecipe } from "../../components/core/basic/Button";
import SummaryCard from "../../components/core/blocks/SummaryCard";
import { PageContainer } from "../../components/core/basic/PageContainer";
import { PricingProgramsTable } from "../../components/custom/programs/PricingProgramsTable";
import { AdvertisingProgramsTable } from "../../components/custom/programs/AdvertisingProgramsTable";
import { useProgramsWithActiveAdCounts } from "../../components/custom/programs/bidding/useProgramsWithActiveAdCounts";
import { usePrograms } from "../../modules/programs/api/usePrograms";
import { type ProgramEngine } from "../../zodios/apiDefinitions/api/programs";
import { useSimpleCan } from "../../config/casl/casl";

// One programs list per engine. Each owns its fetch and permission.

const createLinkClassName = buttonRecipe();

const AdvertisingCreateLink = ({ engine }: { engine: ProgramEngine }) => {
  const { t } = useTranslation(["programs"]);
  return (
    <Link to="/user/advertising/programs/create" search={{ engine }} className={createLinkClassName}>
      {t(($) => $.programs["Create New Program"])}
    </Link>
  );
};

/** The pricing programs page, for /user/programs and /user/advertising/programs. */
export const PricingProgramsPage = () => {
  const { t } = useTranslation(["programs"]);
  const title = t(($) => $.programs["Pricing Program"]);
  const { data, isLoading } = usePrograms({ engine: "pricing" }, { refetchOnMount: "always" });

  return (
    <PageContainer>
      <SummaryCard title={title}>
        <PricingProgramsTable
          isLoading={isLoading}
          programs={data}
          title={title}
          persistKey="programList-pricing"
          toolbarAction={
            <Link to="/user/programs/create" className={createLinkClassName}>
              {t(($) => $.programs["Create New Program"])}
            </Link>
          }
        />
      </SummaryCard>
    </PageContainer>
  );
};

export const BiddingProgramList = () => {
  const { t } = useTranslation(["programs"]);
  const canEdit = useSimpleCan("update", "Ad");
  const { data, isLoading } = usePrograms({ engine: "bidding" }, { refetchOnMount: "always" });
  const programs = useProgramsWithActiveAdCounts(data);

  return (
    <AdvertisingProgramsTable
      isLoading={isLoading}
      programs={programs}
      title={t(($) => $.programs["Bidding Program"])}
      persistKey="programList-bidding"
      canEdit={canEdit}
      showActiveAds
      showClone
      toolbarAction={<AdvertisingCreateLink engine="bidding" />}
    />
  );
};

export const BudgetProgramList = () => {
  const { t } = useTranslation(["programs"]);
  const canEdit = useSimpleCan("update", "Ad");
  const { data, isLoading } = usePrograms({ engine: "budget" }, { refetchOnMount: "always" });

  return (
    <AdvertisingProgramsTable
      isLoading={isLoading}
      programs={data}
      title={t(($) => $.programs["Campaign Program"])}
      persistKey="programList-budget"
      canEdit={canEdit}
      showClone
      toolbarAction={<AdvertisingCreateLink engine="budget" />}
    />
  );
};

export const LineItemBiddingProgramList = () => {
  const { t } = useTranslation(["programs"]);
  const canEdit = useSimpleCan("update", "DSPEntity");
  const { data, isLoading } = usePrograms({ engine: "lineitem_bidding" }, { refetchOnMount: "always" });

  return (
    <AdvertisingProgramsTable
      isLoading={isLoading}
      programs={data}
      title={t(($) => $.programs["Line Item Bidding Program"])}
      persistKey="programList-lineitem_bidding"
      canEdit={canEdit}
      toolbarAction={<AdvertisingCreateLink engine="lineitem_bidding" />}
    />
  );
};
